// frontend/packages/business-console/src/pages/Inbox.jsx
//
// Unified per-business inbox. Two tabs only — Open (default) and Archived.
// The legacy "All" tab is gone: it added a third choice without a job
// (its content == Open ∪ Archived). Archived items are the explicit
// "I want to hide this" state and live on their own page; deleted
// items are invisible everywhere (soft-deleted via deletedAt on the
// backend).

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { BusinessContext } from '../contexts/BusinessContext';
import useMobileDetect from '../hooks/useMobileDetect';
import {
  listConversations,
  getInboxSummary,
  deleteConversation,
} from '../services/inboxService';
import ConversationListItem from '../components/inbox/ConversationListItem';
import InboxConversation from './InboxConversation';
import { useTokens, Surface, PageHeader } from '../components/primitives';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

const TABS = [
  { key: 'open', label: 'Open', filter: { status: 'open' } },
  { key: 'archived', label: 'Archived', filter: { status: 'archived' } },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DELETE_CONFIRM_PHRASE = 'DELETE';

// The date/source/per-page filter fields, shared by the desktop inline row
// and the mobile bottom-sheet so there's one source of truth. `fullWidth`
// stacks them for the sheet; otherwise they sit inline with fixed min-widths.
function FilterControls({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  sourceFilter,
  setSourceFilter,
  pageSize,
  setPageSize,
  fullWidth = false,
}) {
  const sizeSx = (w) => (fullWidth ? undefined : { minWidth: w });
  return (
    <>
      <TextField
        type="date"
        size="small"
        label="From"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth={fullWidth}
        sx={sizeSx(160)}
      />
      <TextField
        type="date"
        size="small"
        label="To"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth={fullWidth}
        sx={sizeSx(160)}
      />
      <FormControl size="small" fullWidth={fullWidth} sx={sizeSx(150)}>
        <InputLabel id="source-filter-label">Source</InputLabel>
        <Select
          labelId="source-filter-label"
          label="Source"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          <MenuItem value="primary">Primary inbox</MenuItem>
          <MenuItem value="contactForm">Contact form</MenuItem>
          <MenuItem value="freeAudit">Free audit</MenuItem>
          <MenuItem value="all">All (incl. free audits)</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth={fullWidth} sx={sizeSx(120)}>
        <InputLabel id="page-size-label">Per page</InputLabel>
        <Select
          labelId="page-size-label"
          label="Per page"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
}

export default function Inbox() {
  const { business } = useContext(BusinessContext);
  const t = useTokens();
  const { isMobile } = useMobileDetect();
  const basePath = useMemo(
    () => (business?.slug ? `/business/${business.slug}` : ''),
    [business?.slug]
  );

  // Desktop split view: the selected conversation renders in the right pane
  // (no navigation). On mobile the rows navigate to the full-screen thread.
  const [selectedId, setSelectedId] = useState(null);

  const [tabKey, setTabKey] = useState('open');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // 'primary' (default; hides freeAudit) | 'contactForm' | 'freeAudit' |
  // 'all' (escape hatch; includes freeAudit). 'primary' sends no source
  // param and lets the backend's default-exclude kick in.
  const [sourceFilter, setSourceFilter] = useState('primary');
  const [pageSize, setPageSize] = useState(25);

  // cursorStack is the trail of "before" cursors for pages we've already
  // walked forward through, so Previous can rewind without re-running an
  // offset query. Each entry is the `before` value used to fetch a page.
  const [cursorStack, setCursorStack] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    unread: 0,
    open: 0,
    freeAuditUnread: 0,
    freeAuditOpen: 0,
    quarantined: 0,
  });

  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Mobile bottom-sheet for the date/source/per-page filters (they bury the
  // message list if rendered inline on a phone).
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Debounce search input — 300ms feels right for typed queries on mobile.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Any filter change resets to page 0. Tab change does too — switching from
  // Open to Archived shouldn't land you on page 3 of Archived with stale
  // cursors from the Open tab.
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [tabKey, debouncedSearch, startDate, endDate, sourceFilter, pageSize]);

  const buildParams = useCallback(
    (cursor) => {
      const tab = TABS.find((t) => t.key === tabKey) || TABS[0];
      const params = { ...tab.filter, limit: pageSize };
      if (debouncedSearch) params.q = debouncedSearch;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      // 'primary' means "use backend default (excludes freeAudit)"; send no
      // source param. Anything else (contactForm | freeAudit | all) is
      // forwarded verbatim — 'all' is the explicit escape hatch.
      if (sourceFilter !== 'primary') params.source = sourceFilter;
      if (cursor) params.before = cursor;
      return params;
    },
    [tabKey, debouncedSearch, startDate, endDate, sourceFilter, pageSize]
  );

  const fetchPage = useCallback(
    async ({ silent = false, cursor = null } = {}) => {
      if (!silent) setLoading(true);
      setError('');
      try {
        const result = await listConversations(buildParams(cursor));
        setItems(result.items || []);
        setHasMore(Boolean(result.hasMore));
        setNextCursor(result.nextCursor || null);
      } catch (err) {
        setError(
          err?.response?.data?.message || err?.message || 'Failed to load inbox'
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [buildParams]
  );

  // Whenever the page index or any filter dependency changes, fetch the
  // matching page using the cursor at that index.
  useEffect(() => {
    fetchPage({ cursor: cursorStack[pageIndex] || null });
  }, [fetchPage, cursorStack, pageIndex]);

  const refreshSummary = useCallback(async () => {
    try {
      const s = await getInboxSummary();
      setSummary(s);
    } catch {
      // Non-fatal; summary is just a badge.
    }
  }, []);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  // Polling: refresh the current page every 30s while the tab is visible.
  useEffect(() => {
    let timer = null;
    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchPage({ silent: true, cursor: cursorStack[pageIndex] || null });
        refreshSummary();
      }
    };
    timer = setInterval(tick, 30000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [fetchPage, refreshSummary, cursorStack, pageIndex]);

  // Desktop split: keep a conversation selected for the right pane. Default to
  // the first row; re-pick if the current selection scrolled off the page.
  useEffect(() => {
    if (isMobile || !items.length) {
      setSelectedId((cur) => (isMobile ? null : cur));
      return;
    }
    setSelectedId((cur) =>
      cur && items.some((c) => c.id === cur) ? cur : items[0].id
    );
  }, [isMobile, items]);

  const goNext = () => {
    if (!hasMore || !nextCursor) return;
    // If we've never been past pageIndex+1, append the new cursor; otherwise
    // we're walking back through a previously-cached trail.
    setCursorStack((stack) => {
      if (pageIndex + 1 >= stack.length) return [...stack, nextCursor];
      return stack;
    });
    setPageIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (pageIndex === 0) return;
    setPageIndex((i) => i - 1);
  };

  const activeFilterCount =
    (debouncedSearch ? 1 : 0) +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0) +
    (sourceFilter !== 'primary' ? 1 : 0);

  // The Filters button badge counts only what lives inside the sheet (search
  // stays visible on its own), so it reflects the hidden state.
  const sheetFilterCount =
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0) +
    (sourceFilter !== 'primary' ? 1 : 0);

  const filterControlProps = {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    sourceFilter,
    setSourceFilter,
    pageSize,
    setPageSize,
  };

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setSourceFilter('all');
  };

  const requestDelete = (conversation) => {
    setDeleteTarget({
      id: conversation.id,
      name:
        conversation.customer?.name ||
        conversation.customer?.email ||
        'this conversation',
    });
    setDeleteConfirm('');
  };

  const cancelDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirm('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteConfirm !== DELETE_CONFIRM_PHRASE) return;
    setDeleting(true);
    try {
      await deleteConversation(deleteTarget.id);
      // Optimistically remove from the rendered list; if we end up on an
      // empty page, step back so the user isn't stuck staring at nothing.
      setItems((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirm('');
      // Refetch in the background to keep hasMore / nextCursor honest.
      fetchPage({ silent: true, cursor: cursorStack[pageIndex] || null });
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to delete'
      );
    } finally {
      setDeleting(false);
    }
  };

  const headerMeta = (
    <Typography sx={{ fontSize: 13, color: t.muted, fontWeight: 600 }}>
      {summary.unread > 0
        ? `${summary.unread} unread · ${summary.open} open`
        : `${summary.open} open conversation${summary.open === 1 ? '' : 's'}`}
      {summary.quarantined > 0 ? ` · ${summary.quarantined} quarantined` : ''}
    </Typography>
  );

  const emptyState = (
    <Box
      sx={{
        py: 8,
        textAlign: 'center',
        color: 'text.secondary',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />
      <Typography variant="subtitle1">No conversations</Typography>
      <Typography variant="body2">
        {debouncedSearch || startDate || endDate
          ? 'Try a different filter or search.'
          : tabKey === 'archived'
            ? 'Nothing archived yet.'
            : 'When customers fill out your contact form, their message will appear here.'}
      </Typography>
    </Box>
  );

  const paginationRow = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 1.5, py: 1, borderTop: `1px solid ${t.hairline}` }}
    >
      <Typography variant="caption" sx={{ color: t.muted }}>
        Page {pageIndex + 1}
        {hasMore ? '' : pageIndex > 0 ? ' · end' : ''}
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <Button
          size="small"
          startIcon={<ChevronLeftIcon />}
          disabled={pageIndex === 0}
          onClick={goPrev}
          sx={{ textTransform: 'none', minWidth: 0 }}
        >
          Prev
        </Button>
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          disabled={!hasMore}
          onClick={goNext}
          sx={{ textTransform: 'none', minWidth: 0 }}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );

  // The conversation list, shared by the desktop list pane and the mobile
  // single column. `interactive` selects in place (desktop) vs navigating.
  const renderList = (interactive) =>
    items.map((c) => (
      <ConversationListItem
        key={c.id}
        conversation={c}
        basePath={basePath}
        selected={interactive && selectedId === c.id}
        onSelect={interactive ? setSelectedId : undefined}
        onDelete={tabKey === 'archived' ? () => requestDelete(c) : null}
      />
    ));

  return (
    <Box>
      <PageHeader
        title="Inbox"
        meta={headerMeta}
        sx={{ mb: 1.5 }}
        actions={
          <Button
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => {
              fetchPage({ cursor: cursorStack[pageIndex] || null });
              refreshSummary();
            }}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        }
      />

      {/* Sticky: tabs + search (always visible) + a mobile Filters button.
          The date/source/per-page controls live inline on desktop and in a
          bottom-sheet on mobile so they don't bury the message list. */}
      <Box
        sx={{
          mb: 1.5,
          position: 'sticky',
          top: { xs: 56, md: 64 },
          zIndex: 1,
          backgroundColor: 'background.default',
          py: 1,
        }}
      >
        <Tabs
          value={tabKey}
          onChange={(_, v) => setTabKey(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="Search name, email, phone…"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={() => setFiltersOpen(true)}
            aria-label="Filters"
            sx={{ display: { sm: 'none' }, border: 1, borderColor: 'divider' }}
          >
            <Badge badgeContent={sheetFilterCount} color="primary">
              <FilterAltOutlinedIcon />
            </Badge>
          </IconButton>
        </Stack>

        {/* Desktop inline filters. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 1.5, display: { xs: 'none', sm: 'flex' } }}
        >
          <FilterControls {...filterControlProps} />
          {activeFilterCount > 0 && (
            <Button
              size="small"
              startIcon={<FilterAltOutlinedIcon />}
              onClick={clearFilters}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Clear ({activeFilterCount})
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Desktop: split view (list pane + thread pane) ── */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1.5,
              alignItems: 'stretch',
              height: 'calc(100vh - 270px)',
              minHeight: 460,
            }}
          >
            <Surface
              radius="14px"
              sx={{
                width: 360,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 0,
              }}
            >
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {items.length === 0 ? emptyState : renderList(true)}
              </Box>
              {items.length > 0 ? paginationRow : null}
            </Surface>

            <Surface
              radius="14px"
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 0,
              }}
            >
              {selectedId ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    px: 2.5,
                    pt: 1,
                  }}
                >
                  <InboxConversation
                    key={selectedId}
                    conversationId={selectedId}
                    embedded
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: t.faint,
                  }}
                >
                  <ForumOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Select a conversation to read and reply
                  </Typography>
                </Box>
              )}
            </Surface>
          </Box>

          {/* ── Mobile: single column, rows navigate to the full thread ── */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {items.length === 0 ? (
              emptyState
            ) : (
              <>
                <Surface sx={{ overflow: 'hidden' }}>
                  {renderList(false)}
                </Surface>
                <Box sx={{ mt: 1.5 }}>{paginationRow}</Box>
              </>
            )}
          </Box>
        </>
      )}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Delete conversation
          <IconButton size="small" onClick={cancelDelete} disabled={deleting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This permanently hides the conversation with{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {deleteTarget?.name}
            </Box>{' '}
            from the inbox. The record stays in the database for compliance, but
            there is no in-app way to restore it.
          </DialogContentText>
          <DialogContentText sx={{ mb: 1, fontSize: 14 }}>
            Type{' '}
            <Box
              component="span"
              sx={{ fontFamily: 'monospace', fontWeight: 700 }}
            >
              {DELETE_CONFIRM_PHRASE}
            </Box>{' '}
            to confirm.
          </DialogContentText>
          <TextField
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            placeholder={DELETE_CONFIRM_PHRASE}
            disabled={deleting}
            inputProps={{ 'aria-label': 'Type DELETE to confirm' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={cancelDelete}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleting || deleteConfirm !== DELETE_CONFIRM_PHRASE}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile filter bottom-sheet — thumb-reachable, keeps the message list
          at the top of the screen on a phone. */}
      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={() => setFiltersOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="column" spacing={2}>
          <FilterControls {...filterControlProps} fullWidth />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={clearFilters}
              disabled={activeFilterCount === 0}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              onClick={() => setFiltersOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
}
